import { useEffect, useState, useCallback, useRef } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useDispatch, useSelector } from "react-redux";
import { useSearchParams } from "react-router-dom";
import { RootState } from "@/app/rootReducer";
import { AppDispatch } from "@/app/store";
import {
  trackResourceFiltersChanged,
  trackResourceSortChanged,
} from "@/modules/hub/features/ResourceLibrary/analytics/resourceLibraryAnalytics";
import {
  fetchResources,
  fetchMoreResources,
  setResources,
  setPage,
  setHasMore,
  setLastFetchParams,
  setShowOnlyFavorites,
  setShowOnlyFeatured,
  setShowOnlySharedWithMe,
  setSelectedTrades,
  setSelectedResourceTypes,
  setSelectedResourceCategories,
  setSelectedBusinessRoles,
  setSelectedProcessWheels,
  setIsRestoringState,
  clearFilters,
} from "@/modules/hub/features/ResourceLibrary/slices/resourceLibrarySlice";
import { getResourceLibraryMetadataAction } from "@/modules/hub/features/ResourceLibrary/slices/resourceLibraryMetadataSlice";
import {
  FilterSidebarFormData,
  FilterSidebarFormSchema,
} from "@/modules/hub/features/ResourceLibrary/hooks/FilterSidebarFormSchema";
import { getFavoritesKey } from "@/api/getFavoritesKey/getFavoritesKeyApi";
import { getSharedWithMeKey } from "@/api/getSharedWithMeKey/getSharedWithMeKeyApi";
import { searchService } from "@/modules/hub/features/Search/services/searchService";
import { resourceLibraryAlgoliaService } from "@/modules/hub/features/ResourceLibrary/services/resourceLibraryAlgoliaService";
import {
  Trade,
  BusinessRole,
  ProcessWheelFacet,
} from "@/modules/hub/features/ResourceLibrary/api/getResourceLibraryMetadata/types";
import { ResourceTypeFacet } from "@/api/getResourceSearchResults/types";
import { ResourceCategory } from "@/api/fetchCreateUpdateResourceMetadata/types";

export function useResourcesLibrary(
  context: "library" | "library-v2" = "library",
) {
  const dispatch = useDispatch<AppDispatch>();
  const [searchParams] = useSearchParams();

  const {
    resources,
    page,
    hasMore,
    totalCount,
    lastFetchParams,
    scrollPosition,
    searchInput,
    showOnlyFavorites,
    showOnlyFeatured,
    showOnlySharedWithMe,
    selectedTrades,
    selectedResourceTypes,
    selectedResourceCategories,
    selectedBusinessRoles,
    selectedProcessWheels,
    isFetching,
    availableFacets,
  } = useSelector((state: RootState) => state.resourceLibrary);

  const { metadata, loadingGet } = useSelector(
    (state: RootState) => state.resourceLibraryMetadata,
  );

  const urlShowFavorites = searchParams.get("showFavorites") === "true";
  const urlShowSharedWithMe = searchParams.get("showSharedWithMe") === "true";

  const form = useForm<FilterSidebarFormData>({
    resolver: zodResolver(FilterSidebarFormSchema),
    defaultValues: {
      isFavoriteOnly: showOnlyFavorites || urlShowFavorites || false,
      isFeaturedOnly: showOnlyFeatured || false,
      isSharedWithMeOnly: showOnlySharedWithMe || urlShowSharedWithMe || false,
      trades: selectedTrades || [],
      contentTypes: selectedResourceTypes || [],
      categories: selectedResourceCategories || [],
      businessRoles: selectedBusinessRoles || [],
      processWheels: selectedProcessWheels || [],
    },
    mode: "onChange",
  });

  const [isInitialLoading, setIsInitialLoading] = useState(true);
  const [favoriteUuids, setFavoriteUuids] = useState<Set<string>>(new Set());
  const [sharedWithMeUuids, setSharedWithMeUuids] = useState<Set<string>>(
    new Set(),
  );
  const [loading, setLoading] = useState(false);
  const [isInitialSortLoad, setIsInitialSortLoad] = useState(true);
  const hasTrackedInitialFilters = useRef(false);

  type SortOption = "newest" | "oldest" | "mostViewed" | "alphabetical";
  const [sortOption, setSortOption] = useState<SortOption>("newest");

  const sortMappings: Record<
    SortOption,
    { sortBy: string; sortOrder: string }
  > = {
    newest: { sortBy: "publishStartDate", sortOrder: "desc" },
    oldest: { sortBy: "publishStartDate", sortOrder: "asc" },
    mostViewed: { sortBy: "viewCount", sortOrder: "desc" },
    alphabetical: { sortBy: "title", sortOrder: "asc" },
  };

  // Sync form to Redux
  useEffect(() => {
    const subscription = form.watch((values) => {
      dispatch(setShowOnlyFavorites(values.isFavoriteOnly || false));
      dispatch(setShowOnlyFeatured(values.isFeaturedOnly || false));
      dispatch(setShowOnlySharedWithMe(values.isSharedWithMeOnly || false));
      dispatch(
        setSelectedTrades((values.trades ?? []).filter((t): t is Trade => !!t)),
      );
      dispatch(
        setSelectedResourceTypes(
          (values.contentTypes ?? []).filter(
            (t): t is ResourceTypeFacet => !!t,
          ),
        ),
      );
      dispatch(
        setSelectedResourceCategories(
          (values.categories ?? []).filter((c): c is ResourceCategory => !!c),
        ),
      );
      dispatch(
        setSelectedBusinessRoles(
          (values.businessRoles ?? []).filter((e): e is BusinessRole => !!e),
        ),
      );
      dispatch(
        setSelectedProcessWheels(
          (values.processWheels ?? []).filter(
            (p): p is ProcessWheelFacet => !!p,
          ),
        ),
      );
    });
    return () => subscription.unsubscribe();
  }, [form, dispatch]);

  const buildRequestData = useCallback(() => {
    const values = form.getValues();
    const { sortBy, sortOrder } = sortMappings[sortOption];

    return {
      showFavorites: values.isFavoriteOnly || undefined,
      showFeatured: values.isFeaturedOnly || undefined,
      showSharedWithMe: values.isSharedWithMeOnly || undefined,
      tradeIds: values.trades?.map((t) => t.id),
      categoryIds: values.categories?.map((c) => c.id),
      businessRoleIds: values.businessRoles?.map((e) => e.id),
      // Pass the actual names for Algolia filtering
      tradeNames: values.trades?.map((t) => t.name),
      resourceTypeNames:
        values.contentTypes && values.contentTypes.length > 0
          ? values.contentTypes.map((type) => type.name)
          : undefined, // Only send if explicitly selected
      categoryNames: values.categories?.map((c) => c.name),
      businessRoleNames: values.businessRoles?.map((e) => e.name),
      processWheelNames: values.processWheels?.map((p) => p.name),
      sortBy,
      sortOrder,
      page: 1,
      // 100/page: with 10, tall monitors never overflowed the scroll
      // container, so infinite scroll's trigger never fired (#9726).
      pageSize: 100,
    };
  }, [form, metadata, sortOption]);

  const isStateRestorable = useCallback(() => {
    if (!lastFetchParams) return false;

    const getFiltersWithoutPagination = (params: Record<string, unknown>) => {
      const paginationKeys = ["page", "pageSize"];
      return Object.fromEntries(
        Object.entries(params).filter(([key]) => !paginationKeys.includes(key)),
      );
    };

    const currentFilters = getFiltersWithoutPagination(buildRequestData());
    const previousFilters = getFiltersWithoutPagination(lastFetchParams);

    return JSON.stringify(currentFilters) === JSON.stringify(previousFilters);
  }, [lastFetchParams, buildRequestData]);

  // Select the secured-key context for the active version (V2 = stage-gated). Runs before
  // the metadata/search effects below so those thunks use the right context, and resets to
  // "library" when navigating back to V1.
  useEffect(() => {
    resourceLibraryAlgoliaService.setContext(context);
  }, [context]);

  useEffect(() => {
    dispatch(getResourceLibraryMetadataAction());
  }, [dispatch]);

  // Fetch favorites on mount
  useEffect(() => {
    const fetchFavorites = async () => {
      try {
        const favoritesData = await getFavoritesKey(context);
        setFavoriteUuids(new Set(favoritesData.favoriteResourceUuids));
        searchService.updateFavoritesKey(
          favoritesData.favoritesSecuredApiKey,
          favoritesData.favoriteResourceUuids,
        );
      } catch (error) {
        console.error("[ResourceLibrary] Error fetching favorites:", error);
      }
    };

    fetchFavorites();
  }, [context]);

  // Fetch shared-with-me key on mount
  useEffect(() => {
    const fetchSharedWithMe = async () => {
      try {
        const sharedData = await getSharedWithMeKey(context);
        setSharedWithMeUuids(new Set(sharedData.sharedResourceUuids));
        searchService.updateSharedWithMeKey(
          sharedData.sharedWithMeSecuredApiKey,
          sharedData.sharedResourceUuids,
        );
      } catch (error) {
        console.error(
          "[ResourceLibrary] Error fetching shared resources:",
          error,
        );
      }
    };

    fetchSharedWithMe();
  }, [context]);

  // Function to refresh favorites (called after toggle)
  const refreshFavorites = useCallback(async () => {
    try {
      const favoritesData = await getFavoritesKey(context);

      // Update local state with server data
      setFavoriteUuids(new Set(favoritesData.favoriteResourceUuids));

      // Update search service with new key
      searchService.updateFavoritesKey(
        favoritesData.favoritesSecuredApiKey,
        favoritesData.favoriteResourceUuids,
      );

      return favoritesData;
    } catch (error) {
      console.error("[ResourceLibrary] Error refreshing favorites:", error);
      throw error;
    }
  }, [context]);

  // Function to refresh shared-with-me (called after removing a share)
  const refreshSharedWithMe = useCallback(async () => {
    try {
      const sharedData = await getSharedWithMeKey(context);

      // Update local state with server data
      setSharedWithMeUuids(new Set(sharedData.sharedResourceUuids));

      // Update search service with new key
      searchService.updateSharedWithMeKey(
        sharedData.sharedWithMeSecuredApiKey,
        sharedData.sharedResourceUuids,
      );

      return sharedData;
    } catch (error) {
      console.error(
        "[ResourceLibrary] Error refreshing shared resources:",
        error,
      );
      throw error;
    }
  }, [context]);

  // Session storage key for scroll restoration
  const SCROLL_STORAGE_KEY = "resourceLibrary_scrollState";

  // Restore scroll position when component mounts (e.g., navigating back)
  useEffect(() => {
    // Check if we have a saved scroll position
    const savedState = sessionStorage.getItem(SCROLL_STORAGE_KEY);

    if (savedState) {
      try {
        const { scrollY, timestamp } = JSON.parse(savedState);

        // Only restore if saved within last 5 minutes (prevent stale data)
        const timeDiff = Date.now() - timestamp;

        if (timeDiff < 5 * 60 * 1000) {
          // Wait for document to be tall enough for our scroll position
          let attempts = 0;
          const maxAttempts = 100; // 10 seconds max

          const checkAndScroll = () => {
            attempts++;
            const currentHeight = document.body.scrollHeight;

            // Can we scroll to the saved position?
            if (currentHeight > scrollY) {
              // Yes! Document is tall enough
              window.scrollTo(0, scrollY);
              sessionStorage.removeItem(SCROLL_STORAGE_KEY);
            } else if (attempts < maxAttempts) {
              // Not tall enough yet, check again soon
              setTimeout(checkAndScroll, 100);
            } else {
              // Give up after max attempts
              sessionStorage.removeItem(SCROLL_STORAGE_KEY);
            }
          };

          // Start checking immediately
          checkAndScroll();
        }
      } catch (e) {
        // Failed to parse saved state, just clear it
        sessionStorage.removeItem(SCROLL_STORAGE_KEY);
      }
    }
  }, []); // Run once on mount

  // Note: Scroll position is saved in ResourceCard/ResourceListItem when clicking

  // Create stable values for watch dependencies (before using them in effects)
  const isFavoriteOnly = form.watch("isFavoriteOnly");
  const isFeaturedOnly = form.watch("isFeaturedOnly");
  const isSharedWithMeOnly = form.watch("isSharedWithMeOnly");
  const contentTypes = form.watch("contentTypes");
  const trades = form.watch("trades");
  const categories = form.watch("categories");
  const businessRoles = form.watch("businessRoles");
  const processWheels = form.watch("processWheels");

  // Clear saved scroll when filters change (new search)
  useEffect(() => {
    sessionStorage.removeItem(SCROLL_STORAGE_KEY);
  }, [
    isFavoriteOnly,
    isFeaturedOnly,
    isSharedWithMeOnly,
    JSON.stringify(contentTypes),
    JSON.stringify(trades),
    JSON.stringify(categories),
    JSON.stringify(businessRoles),
    JSON.stringify(processWheels),
    sortOption,
  ]);

  useEffect(() => {
    const load = async () => {
      if (!metadata) return;

      if (resources.length > 0 && lastFetchParams && isStateRestorable()) {
        dispatch(setIsRestoringState(true));
        setIsInitialLoading(false);
        return;
      }

      const request = buildRequestData();
      setLoading(true);
      dispatch(setResources([]));
      dispatch(setPage(1));
      dispatch(setHasMore(true));

      await dispatch(fetchResources(request));

      dispatch(setLastFetchParams(request));
      setLoading(false);
      setIsInitialLoading(false);
    };
    load();
  }, [metadata]);

  useEffect(() => {
    if (isInitialLoading || !metadata) return;

    const handleFilterChange = async () => {
      const values = form.getValues();

      // If favorites filter is being turned ON, refresh favorites key first
      if (values.isFavoriteOnly) {
        try {
          const favoritesData = await getFavoritesKey(context);
          setFavoriteUuids(new Set(favoritesData.favoriteResourceUuids));
          searchService.updateFavoritesKey(
            favoritesData.favoritesSecuredApiKey,
            favoritesData.favoriteResourceUuids,
          );
        } catch (error) {
          console.error(
            "[ResourceLibrary] Failed to refresh favorites key:",
            error,
          );
        }
      }

      // If shared-with-me filter is being turned ON, refresh shared-with-me key first
      if (values.isSharedWithMeOnly) {
        try {
          const sharedData = await getSharedWithMeKey(context);
          setSharedWithMeUuids(new Set(sharedData.sharedResourceUuids));
          searchService.updateSharedWithMeKey(
            sharedData.sharedWithMeSecuredApiKey,
            sharedData.sharedResourceUuids,
          );
        } catch (error) {
          console.error(
            "[ResourceLibrary] Failed to refresh shared-with-me key:",
            error,
          );
        }
      }

      const request = buildRequestData();
      setLoading(true);
      dispatch(setResources([]));
      dispatch(setPage(1));
      dispatch(setHasMore(true));
      await dispatch(fetchResources(request));
      dispatch(setLastFetchParams(request));
      setLoading(false);
    };

    handleFilterChange();
  }, [
    isFavoriteOnly,
    isFeaturedOnly,
    isSharedWithMeOnly,
    JSON.stringify(contentTypes),
    JSON.stringify(trades),
    JSON.stringify(categories),
    JSON.stringify(businessRoles),
    JSON.stringify(processWheels),
    sortOption,
    context,
  ]);

  // Track filter changes for analytics
  useEffect(() => {
    if (isInitialLoading) return;

    if (!hasTrackedInitialFilters.current) {
      hasTrackedInitialFilters.current = true;
      return;
    }

    trackResourceFiltersChanged();
  }, [
    isFavoriteOnly,
    isFeaturedOnly,
    isSharedWithMeOnly,
    JSON.stringify(contentTypes),
    JSON.stringify(trades),
    JSON.stringify(categories),
    JSON.stringify(businessRoles),
    JSON.stringify(processWheels),
    sortOption,
    isInitialLoading,
  ]);

  // Track sort changes for analytics
  useEffect(() => {
    if (isInitialSortLoad) {
      setIsInitialSortLoad(false);
      return;
    }
    trackResourceSortChanged({ sortOption });
  }, [sortOption]);

  const fetchMore = useCallback(() => {
    const nextPage = page + 1;
    const request = { ...buildRequestData(), page: nextPage };
    dispatch(setPage(nextPage));
    dispatch(fetchMoreResources(request));
  }, [page, dispatch, buildRequestData]);

  const handleClearFilters = () => {
    dispatch(clearFilters());

    form.reset({
      isFavoriteOnly: false,
      isFeaturedOnly: false,
      isSharedWithMeOnly: false,
      trades: [],
      contentTypes: [],
      categories: [],
      businessRoles: [],
      processWheels: [],
    });
  };

  const setFeaturedOnly = () => {
    form.setValue("isFeaturedOnly", true);
  };

  // Use dynamic facets from search results, fall back to static metadata if no search has been done yet.
  // Trades need a second-level scope: Algolia's multi-valued `trades` facet bucket counts every
  // value in a matching record's trades array, so a resource co-tagged with a trade another
  // company owns would leak that trade into this company's sidebar. Intersect against the
  // company's own trade names (shipped via the secured search config). CertainPath sees all.
  const allowedTradeNames = new Set(searchService.getUserTradeNames());
  const isCertainPath = searchService.isCertainPath();
  const dynamicMetadata = availableFacets
    ? {
        trades: isCertainPath
          ? availableFacets.trades
          : availableFacets.trades.filter((t) => allowedTradeNames.has(t.name)),
        businessRoles: availableFacets.businessRoles.map((b) => ({
          id: b.id,
          name: b.name,
          description: b.description,
          // Include count for display
          count: b.count,
        })),
        resourceTypes: availableFacets.resourceTypes.map((r) => ({
          id: r.id,
          name: r.name,
          resourceCount: r.resourceCount,
          icon: r.icon,
        })),
        resourceCategories: availableFacets.categories.map((c) => ({
          id: c.id,
          name: c.name,
          count: c.count,
        })),
        processWheels: availableFacets.processWheels.map((p) => ({
          id: p.id,
          name: p.name,
          count: p.count,
        })),
      }
    : metadata;

  // Merge favorite status with resources
  const resourcesWithFavorites = resources.map((resource) => ({
    ...resource,
    isFavorited: favoriteUuids.has(resource.uuid),
  }));

  // Handle favorite toggle
  const handleFavoriteToggle = useCallback(
    async (resourceUuid: string) => {
      // Optimistic update
      const newFavorites = new Set(favoriteUuids);
      const wasInFavorites = newFavorites.has(resourceUuid);

      if (wasInFavorites) {
        newFavorites.delete(resourceUuid);
      } else {
        newFavorites.add(resourceUuid);
      }
      setFavoriteUuids(newFavorites);

      // API call happens in ResourceCard/ResourceListItem - wait for it to complete
      // Give the API call time to complete on the server
      // This is a temporary workaround - ideally the toggle API would be awaited
      await new Promise((resolve) => setTimeout(resolve, 500));

      // ALWAYS refresh favorites key after toggle
      // This gets the authoritative list from the server
      try {
        await refreshFavorites();

        // If favorites filter is active, re-fetch resources with new key
        if (isFavoriteOnly) {
          const params = buildRequestData();
          dispatch(fetchResources(params));
        }
      } catch (error) {
        console.error("[ResourceLibrary] Error refreshing favorites:", error);
        // Revert optimistic update on error
        setFavoriteUuids(favoriteUuids);
      }
    },
    [
      favoriteUuids,
      isFavoriteOnly,
      refreshFavorites,
      buildRequestData,
      dispatch,
    ],
  );

  return {
    form,
    page,
    resources: resourcesWithFavorites,
    hasMore,
    totalCount,
    loading: loading || isFetching,
    isInitialLoading,
    fetchMore,
    handleClearFilters,
    setFeaturedOnly,
    sidebarMetadata: dynamicMetadata,
    isMetadataLoading: loadingGet && !availableFacets, // Only loading if we don't have facets yet
    sortOption,
    setSortOption,
    handleFavoriteToggle,
    refreshFavorites,
    refreshSharedWithMe,
  };
}
